/**
 * Task API error messaging.
 *
 * Every task route answers with the same envelope shape but NOT the same key:
 * the completion outcome guard returns `error`, while snooze and update return
 * `message`. Reading only one of them yields an empty toast, which is how a
 * 400 came to produce no feedback at all — the user clicked Complete, saw
 * nothing, and clicked again, producing two identical 400s in the log.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/taskErrors.test.mjs`. Do not add imports.
 */

/**
 * The server's own words for why a task action failed, or a usable fallback.
 *
 * Never returns empty: a silent failure is the bug this module exists to fix.
 */
export function taskErrorMessage(err, fallback = 'Could not complete that action. Please try again.') {
  const data = (err && err.response && err.response.data) || {};
  // `error` first — it is the key the outcome guard uses, and its text names the
  // missing field. `message` is what snooze and update return.
  const raw = data.error || data.message;
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text) return text;
  if (err && typeof err.message === 'string' && err.message.trim() && err.message !== 'Network Error') {
    return err.message.trim();
  }
  return fallback;
}

/** The authoritative outcome list a 400 carries, or null. */
export function allowedOutcomesFrom(err) {
  const data = (err && err.response && err.response.data) || {};
  return Array.isArray(data.allowed) && data.allowed.length ? data.allowed : null;
}

/** Did this fail because an outcome is required? Drives opening the picker. */
export function isOutcomeRequiredError(err) {
  const status = err && err.response && err.response.status;
  if (status !== 400) return false;
  if (allowedOutcomesFrom(err)) return true;
  return /resolution_type/i.test(taskErrorMessage(err, ''));
}
